import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useI18n } from "./i18n";
import { ContentCard } from "./components/ContentCard";
import { FavoriteSave } from "./components/FavoriteSave";
import { HeroSearch } from "./components/HeroSearch";
import { EmptyState } from "@/components/ui/EmptyState";
import {
  attractionsApi,
  destinationsApi,
  eventsApi,
  experiencesApi,
  hotelsApi,
  restaurantsApi,
  reviewsApi,
  searchApi,
  travelGuidesApi,
  nearbyApi,
  recommendationsApi,
  itinerariesApi,
  normalizeList,
  pathForEntityType,
  type CatalogItem,
  type ReviewRow,
} from "@/services/api";
import { useAuth } from "./auth";
import { useLocalePath } from "./hooks";
import type { SaveContentType } from "@/components/ui/SaveButton";

type Item = CatalogItem;

function RelatedLinks({
  title,
  raw,
  pathSegment,
  base,
}: {
  title: string;
  raw: unknown;
  pathSegment: string;
  base: string;
}) {
  if (!Array.isArray(raw) || !raw.length) return null;
  const items = normalizeList(raw as Record<string, unknown>[]);
  if (!items.length) return null;
  return (
    <div className="mt-10">
      <h2 className="font-display text-2xl font-bold">{title}</h2>
      <ul className="mt-4 space-y-2">
        {items.map((n) => (
          <li key={n.id}>
            <Link className="text-pine hover:underline" to={`${base}/${pathSegment}/${n.slug}`}>
              {n.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReviewsBlock({
  entityType,
  entityId,
}: {
  entityType: Exclude<SaveContentType, "itinerary">;
  entityId: string;
}) {
  const { t } = useI18n();
  const { user } = useAuth();
  const base = useLocalePath();
  const [items, setItems] = useState<ReviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingMsg, setPendingMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reviewsApi
      .list(entityType, entityId)
      .then((res) => {
        if (!cancelled) setItems(res.items || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : t("common.error"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [entityType, entityId, t]);

  return (
    <div className="mt-10 border-t border-mist pt-8">
      <h2 className="font-display text-2xl font-bold">{t("reviews.title")}</h2>
      {loading ? <p className="mt-3 text-sm text-ridge">{t("common.loading")}</p> : null}
      {error ? <p className="mt-3 text-sm text-red-700">{error}</p> : null}
      {!loading && !error && !items.length ? (
        <p className="mt-3 text-sm text-ridge">{t("reviews.empty")}</p>
      ) : null}
      <ul className="mt-4 space-y-3">
        {items.map((r) => (
          <li key={r.id} className="rounded-lg border border-mist bg-white p-4">
            <p className="text-sm font-semibold text-ink">{"★".repeat(r.rating)}</p>
            {r.body ? <p className="mt-1 text-sm text-ink/80">{r.body}</p> : null}
          </li>
        ))}
      </ul>
      <div className="mt-6">
        <h3 className="text-sm font-semibold">{t("reviews.write")}</h3>
        {!user ? (
          <p className="mt-2 text-sm text-ridge">
            <Link to={`${base}/login`} className="font-semibold text-pine underline">
              {t("reviews.loginRequired")}
            </Link>
          </p>
        ) : (
          <form
            className="mt-3 space-y-3"
            onSubmit={async (e) => {
              e.preventDefault();
              const fd = new FormData(e.currentTarget);
              const rating = Number(fd.get("rating"));
              const body = String(fd.get("body") || "");
              setBusy(true);
              setPendingMsg(null);
              setError(null);
              try {
                await reviewsApi.create(entityType, entityId, { rating, body });
                setPendingMsg(t("reviews.pending"));
                e.currentTarget.reset();
              } catch (err) {
                setError(err instanceof Error ? err.message : t("common.error"));
              } finally {
                setBusy(false);
              }
            }}
          >
            <label className="block text-sm">
              {t("reviews.rating")}
              <select name="rating" required defaultValue={5} className="mt-1 w-full rounded border border-mist px-3 py-2">
                {[5, 4, 3, 2, 1].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              {t("reviews.body")}
              <textarea name="body" rows={3} className="mt-1 w-full rounded border border-mist px-3 py-2" />
            </label>
            {pendingMsg ? <p className="text-sm text-pine">{pendingMsg}</p> : null}
            <button
              type="submit"
              disabled={busy}
              className="rounded-md bg-pine px-4 py-2 text-sm font-semibold text-sand disabled:opacity-60"
            >
              {t("reviews.submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function favoriteTypeFor(
  kind: string,
): Exclude<SaveContentType, "itinerary"> | null {
  switch (kind) {
    case "destinations":
      return "destination";
    case "attractions":
      return "attraction";
    case "hotels":
      return "hotel";
    case "restaurants":
      return "restaurant";
    case "experiences":
      return "experience";
    case "events":
      return "event";
    default:
      return null;
  }
}

function StateBox({
  loading,
  error,
  empty,
  children,
}: {
  loading: boolean;
  error: string | null;
  empty?: boolean;
  children: React.ReactNode;
}) {
  const { t } = useI18n();
  if (loading) return <p className="py-16 text-center text-ridge">{t("common.loading")}</p>;
  if (error)
    return (
      <EmptyState title={t("common.error")} description={error} />
    );
  if (empty) return <EmptyState title={t("common.empty")} />;
  return <>{children}</>;
}

export function HomePage() {
  const { t, locale } = useI18n();
  const base = useLocalePath();
  const [destinations, setDestinations] = useState<Item[]>([]);
  const [attractions, setAttractions] = useState<Item[]>([]);
  const [experiences, setExperiences] = useState<Item[]>([]);
  const [events, setEvents] = useState<Item[]>([]);
  const [guides, setGuides] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [d, a, e, ev, g] = await Promise.all([
          destinationsApi.list({ language: locale, featured: true, pageSize: 6 }),
          attractionsApi.list({ language: locale, featured: true, pageSize: 6 }),
          experiencesApi.list({ language: locale, featured: true, pageSize: 6 }),
          eventsApi.list({ language: locale, featured: true, pageSize: 6 }),
          travelGuidesApi.list({ language: locale, pageSize: 4 }),
        ]);
        if (cancelled) return;
        setDestinations(normalizeList(d.data.items));
        setAttractions(normalizeList(a.data.items));
        setExperiences(normalizeList(e.data.items));
        setEvents(normalizeList(ev.data.items));
        setGuides(normalizeList(g.data.items));
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : t("common.error"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locale, t]);

  function section(
    title: string,
    href: string,
    items: Item[],
    pathSegment: string,
    favType?: Exclude<SaveContentType, "itinerary">,
  ) {
    if (!items.length && !loading) return null;
    return (
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4">
          <h2 className="font-display text-3xl font-bold">{title}</h2>
          <Link to={`${base}/${href}`} className="text-sm font-semibold text-pine hover:underline">
            {t("home.viewAll")}
          </Link>
        </div>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((d) => (
            <div key={d.id} className="relative">
              <ContentCard
                href={`${base}/${pathSegment}/${d.slug}`}
                title={d.name}
                description={d.description}
                image={d.image}
                demo={d.isDemo}
                demoLabel={t("common.demoListing")}
                meta={[d.city, d.category].filter(Boolean) as string[]}
              />
              {favType ? (
                <div className="absolute end-3 top-3 z-10">
                  <FavoriteSave type={favType} id={d.id} />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="relative isolate min-h-[88vh] overflow-hidden bg-ink">
        <img
          src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&q=80"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/25" />
        <div className="relative z-10 mx-auto flex min-h-[88vh] max-w-7xl flex-col justify-end px-4 pb-16 pt-28 sm:px-6 lg:px-8">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-sun">Kurdistan</p>
          <h1 className="max-w-3xl font-display text-5xl font-bold leading-[1.05] text-sand sm:text-6xl lg:text-7xl">
            {t("home.headline")}
          </h1>
          <p className="mt-4 max-w-xl text-lg text-mist/90 sm:text-xl">{t("home.subheadline")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`${base}/plan-trip`}
              className="rounded-md bg-sun px-6 py-3 text-sm font-semibold text-ink transition hover:brightness-110"
            >
              {t("home.ctaPlan")}
            </Link>
            <Link
              to={`${base}/explore`}
              className="rounded-md border border-sand/40 bg-ink/30 px-6 py-3 text-sm font-semibold text-sand backdrop-blur-sm transition hover:bg-ink/50"
            >
              {t("home.ctaExplore")}
            </Link>
          </div>
          <div className="mt-10 max-w-2xl">
            <HeroSearch />
          </div>
        </div>
      </section>
      {error ? (
        <EmptyState title={t("common.error")} description={error} className="mx-auto max-w-7xl" />
      ) : null}
      {loading ? <p className="py-16 text-center text-ridge">{t("common.loading")}</p> : null}
      {section(t("home.featuredDestinations"), "destinations", destinations, "destinations", "destination")}
      {section(t("home.thingsToDo"), "attractions", attractions, "attractions", "attraction")}
      {section(t("nav.experiences"), "experiences", experiences, "experiences", "experience")}
      {section(t("nav.events"), "events", events, "events", "event")}
      {section(t("nav.travelGuide"), "travel-guide", guides, "travel-guide")}
      <p className="mx-auto max-w-7xl px-4 pb-16 text-center text-sm text-ridge sm:px-6">
        {t("home.trustLine")}
      </p>
    </>
  );
}

function CatalogPage({
  titleKey,
  pathSegment,
  kind,
}: {
  titleKey: string;
  pathSegment: string;
  kind:
    | "destinations"
    | "attractions"
    | "hotels"
    | "restaurants"
    | "experiences"
    | "events"
    | "guides";
}) {
  const { t, locale } = useI18n();
  const base = useLocalePath();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const load = async () => {
      switch (kind) {
        case "destinations":
          return destinationsApi.list({ language: locale, pageSize: 40, q: q || undefined });
        case "attractions":
          return attractionsApi.list({ language: locale, pageSize: 40, q: q || undefined });
        case "hotels":
          return hotelsApi.list({ language: locale, pageSize: 40, q: q || undefined });
        case "restaurants":
          return restaurantsApi.list({ language: locale, pageSize: 40, q: q || undefined });
        case "experiences":
          return experiencesApi.list({ language: locale, pageSize: 40, q: q || undefined });
        case "events":
          return eventsApi.list({ language: locale, pageSize: 40, q: q || undefined });
        case "guides":
          return travelGuidesApi.list({ language: locale, pageSize: 40, q: q || undefined });
      }
    };
    load()
      .then((res) => {
        if (!cancelled) setItems(normalizeList(res.data.items));
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : t("common.error"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [locale, kind, q, t]);

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <h1 className="font-display text-4xl font-bold">{t(titleKey)}</h1>
      <form
        className="mt-6 max-w-md"
        onSubmit={(e) => {
          e.preventDefault();
          const fd = new FormData(e.currentTarget);
          setQ(String(fd.get("q") || "").trim());
        }}
      >
        <input
          name="q"
          defaultValue={q}
          placeholder={t("home.searchPlaceholder")}
          className="w-full rounded-full border border-mist bg-white px-4 py-2.5 text-sm"
        />
      </form>
      <div className="mt-8">
        <StateBox loading={loading} error={error} empty={!items.length}>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => {
              const favType = favoriteTypeFor(kind);
              return (
                <div key={item.id} className="relative">
                  <ContentCard
                    href={`${base}/${pathSegment}/${item.slug}`}
                    title={item.name}
                    description={item.description.slice(0, 160)}
                    image={item.image}
                    demo={item.isDemo}
                    demoLabel={t("common.demoListing")}
                    meta={[item.city || item.category || ""].filter(Boolean)}
                  />
                  {favType ? (
                    <div className="absolute end-3 top-3 z-10">
                      <FavoriteSave type={favType} id={item.id} />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </StateBox>
      </div>
    </section>
  );
}

export function DestinationsPage() {
  return <CatalogPage titleKey="nav.destinations" pathSegment="destinations" kind="destinations" />;
}
export function AttractionsPage() {
  return <CatalogPage titleKey="nav.thingsToDo" pathSegment="attractions" kind="attractions" />;
}
export function HotelsPage() {
  return <CatalogPage titleKey="nav.hotels" pathSegment="hotels" kind="hotels" />;
}
export function RestaurantsPage() {
  return <CatalogPage titleKey="nav.restaurants" pathSegment="restaurants" kind="restaurants" />;
}
export function ExperiencesPage() {
  return <CatalogPage titleKey="nav.experiences" pathSegment="experiences" kind="experiences" />;
}
export function EventsPage() {
  return <CatalogPage titleKey="nav.events" pathSegment="events" kind="events" />;
}
export function TravelGuidePage() {
  return <CatalogPage titleKey="nav.travelGuide" pathSegment="travel-guide" kind="guides" />;
}

function DetailPage({
  load,
  favoriteType,
}: {
  load: (slug: string, language: string) => Promise<Record<string, unknown>>;
  favoriteType?: Exclude<SaveContentType, "itinerary">;
}) {
  const { slug } = useParams();
  const { t, locale } = useI18n();
  const base = useLocalePath();
  const [item, setItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    load(slug, locale)
      .then((data) => {
        if (!cancelled) {
          const n = normalizeList([data])[0];
          setItem(n);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : t("common.error"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, locale, load, t]);

  return (
    <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
      <Link to={base} className="text-sm font-semibold text-pine">
        {t("common.back")}
      </Link>
      <StateBox loading={loading} error={error} empty={!item}>
        {item ? (
          <article className="mt-6">
            {item.image ? (
              <div className="mb-6 overflow-hidden rounded-xl">
                <img src={item.image} alt={item.name} className="h-64 w-full object-cover sm:h-80" />
              </div>
            ) : null}
            <div className="flex flex-wrap items-start justify-between gap-3">
              <h1 className="font-display text-4xl font-bold">{item.name}</h1>
              {favoriteType && item.id ? (
                <FavoriteSave type={favoriteType} id={item.id} />
              ) : null}
            </div>
            {item.city ? <p className="mt-2 text-ridge">{item.city}</p> : null}
            {item.isDemo ? (
              <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-ridge">
                {t("common.demoListing")}
              </p>
            ) : null}
            <p className="mt-6 whitespace-pre-wrap text-base leading-relaxed text-ink/90">
              {item.description}
            </p>
            <RelatedLinks
              title={t("nav.thingsToDo")}
              raw={item.attractions}
              pathSegment="attractions"
              base={base}
            />
            <RelatedLinks
              title={t("nav.hotels")}
              raw={item.hotels}
              pathSegment="hotels"
              base={base}
            />
            <RelatedLinks
              title={t("nav.restaurants")}
              raw={item.restaurants}
              pathSegment="restaurants"
              base={base}
            />
            <RelatedLinks
              title={t("nav.experiences")}
              raw={item.experiences}
              pathSegment="experiences"
              base={base}
            />
            <RelatedLinks
              title={t("nav.events")}
              raw={item.events}
              pathSegment="events"
              base={base}
            />
            {Array.isArray(item.nearby) && (item.nearby as Item[]).length ? (
              <div className="mt-10">
                <h2 className="font-display text-2xl font-bold">{t("common.nearby")}</h2>
                <ul className="mt-4 space-y-2">
                  {(item.nearby as Record<string, unknown>[]).map((n) => {
                    const nn = normalizeList([n])[0];
                    const segment = pathForEntityType(String(nn.type || "destination"));
                    return (
                      <li key={nn.id}>
                        <Link
                          className="text-pine hover:underline"
                          to={`${base}/${segment}/${nn.slug}`}
                        >
                          {nn.name}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
            {favoriteType && item.id ? (
              <ReviewsBlock entityType={favoriteType} entityId={item.id} />
            ) : null}
          </article>
        ) : null}
      </StateBox>
    </section>
  );
}

export function DestinationDetailPage() {
  return (
    <DetailPage
      favoriteType="destination"
      load={(slug, language) => destinationsApi.bySlug(slug, language)}
    />
  );
}
export function AttractionDetailPage() {
  return (
    <DetailPage
      favoriteType="attraction"
      load={(slug, language) => attractionsApi.bySlug(slug, language)}
    />
  );
}
export function HotelDetailPage() {
  return (
    <DetailPage
      favoriteType="hotel"
      load={(slug, language) => hotelsApi.bySlug(slug, language)}
    />
  );
}
export function RestaurantDetailPage() {
  return (
    <DetailPage
      favoriteType="restaurant"
      load={(slug, language) => restaurantsApi.bySlug(slug, language)}
    />
  );
}
export function ExperienceDetailPage() {
  return (
    <DetailPage
      favoriteType="experience"
      load={(slug, language) => experiencesApi.bySlug(slug, language)}
    />
  );
}
export function EventDetailPage() {
  return (
    <DetailPage
      favoriteType="event"
      load={(slug, language) => eventsApi.bySlug(slug, language)}
    />
  );
}
export function TravelGuideDetailPage() {
  return <DetailPage load={(slug, language) => travelGuidesApi.bySlug(slug, language)} />;
}

function detailPathFor(type: string): string {
  return pathForEntityType(type);
}

export function SearchPage() {
  const { t, locale } = useI18n();
  const base = useLocalePath();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const type = params.get("type") || "all";
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!q.trim()) {
      setItems([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    searchApi
      .query({ q, language: locale, type })
      .then((res) => {
        if (!cancelled) setItems(normalizeList(res.data.items));
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : t("common.error"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [q, type, locale, t]);

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <h1 className="font-display text-4xl font-bold">{t("nav.search")}</h1>
      <form
        className="mt-6"
        onSubmit={(e) => {
          e.preventDefault();
          const fd = new FormData(e.currentTarget);
          const next: Record<string, string> = { q: String(fd.get("q") || "") };
          if (type && type !== "all") next.type = type;
          setParams(next);
        }}
      >
        <input
          name="q"
          defaultValue={q}
          placeholder={t("home.searchPlaceholder")}
          className="w-full max-w-xl rounded-full border border-mist bg-white px-5 py-3"
        />
      </form>
      <div className="mt-8">
        <StateBox loading={loading} error={error} empty={!loading && !items.length && !!q}>
          <ul className="space-y-3">
            {items.map((item) => (
              <li key={`${item.type}-${item.id}`} className="rounded-lg border border-mist bg-white p-4">
                <p className="text-xs uppercase tracking-wide text-ridge">{String(item.type)}</p>
                <Link
                  className="font-display text-lg font-semibold text-pine hover:underline"
                  to={`${base}/${detailPathFor(String(item.type))}/${item.slug}`}
                >
                  {item.name}
                </Link>
                <p className="text-sm text-ridge/80">{item.city || ""}</p>
              </li>
            ))}
          </ul>
        </StateBox>
      </div>
    </section>
  );
}

export function PlanTripPage() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const base = useLocalePath();
  const [recs, setRecs] = useState<Item[]>([]);
  const [itineraryId, setItineraryId] = useState<string | null>(null);
  const [savedList, setSavedList] = useState<Record<string, unknown>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [interests, setInterests] = useState<string[]>(["nature", "history", "culture"]);

  useEffect(() => {
    if (!user) return;
    itinerariesApi
      .list()
      .then((res) => setSavedList(res.items || []))
      .catch(() => setSavedList([]));
  }, [user, itineraryId]);

  async function generate(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    try {
      const data = await recommendationsApi.generate(
        {
          interests,
          season: String(fd.get("season") || "spring"),
          startCity: String(fd.get("startCity") || "Erbil"),
          tripDuration: Number(fd.get("days") || 3),
          budget: String(fd.get("budget") || "mid"),
        },
        locale,
      );
      setRecs(normalizeList(data.destinations || []));
      if (user) {
        const created = await itinerariesApi.create({
          name: `Trip from ${fd.get("startCity") || "Erbil"}`,
          durationDays: Number(fd.get("days") || 3),
          startCity: String(fd.get("startCity") || "Erbil"),
          season: String(fd.get("season") || "spring"),
          interests,
        });
        setItineraryId(created.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  const interestOptions = ["nature", "adventure", "history", "culture", "family", "food"];

  return (
    <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <h1 className="font-display text-4xl font-bold">{t("planner.title")}</h1>
      <p className="mt-2 text-ridge">{t("planner.subtitle")}</p>
      <form onSubmit={generate} className="mt-8 space-y-4 rounded-lg border border-mist bg-white p-6">
        <label className="block text-sm">
          {t("planner.days")}
          <input name="days" type="number" min={1} max={14} defaultValue={3} className="mt-1 w-full rounded border border-mist px-3 py-2" />
        </label>
        <label className="block text-sm">
          {t("planner.startCity")}
          <select name="startCity" className="mt-1 w-full rounded border border-mist px-3 py-2" defaultValue="Erbil">
            <option value="Erbil">Erbil</option>
            <option value="Sulaymaniyah">Sulaymaniyah</option>
            <option value="Duhok">Duhok</option>
          </select>
        </label>
        <label className="block text-sm">
          {t("planner.budget")}
          <select name="budget" className="mt-1 w-full rounded border border-mist px-3 py-2">
            <option value="budget">{t("planner.budgetLow")}</option>
            <option value="mid">{t("planner.budgetMid")}</option>
            <option value="luxury">{t("planner.budgetHigh")}</option>
          </select>
        </label>
        <fieldset>
          <legend className="text-sm">{t("planner.interests")}</legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {interestOptions.map((key) => {
              const on = interests.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() =>
                    setInterests((cur) =>
                      on ? cur.filter((x) => x !== key) : [...cur, key],
                    )
                  }
                  className={`rounded-full px-3 py-1.5 text-xs font-medium ${
                    on ? "bg-pine text-sand" : "border border-mist bg-white text-ink"
                  }`}
                >
                  {t(`categories.${key}`)}
                </button>
              );
            })}
          </div>
        </fieldset>
        <label className="block text-sm">
          {t("common.season")}
          <select name="season" className="mt-1 w-full rounded border border-mist px-3 py-2">
            <option value="spring">{t("seasons.spring")}</option>
            <option value="summer">{t("seasons.summer")}</option>
            <option value="autumn">{t("seasons.autumn")}</option>
            <option value="winter">{t("seasons.winter")}</option>
          </select>
        </label>
        <button
          type="submit"
          disabled={busy || !interests.length}
          className="rounded-md bg-pine px-5 py-2.5 text-sm font-semibold text-sand disabled:opacity-60"
        >
          {busy ? t("common.loading") : t("planner.generate")}
        </button>
        {!user ? (
          <p className="text-sm text-ridge">
            <Link className="text-pine underline" to={`${base}/login`}>
              {t("nav.login")}
            </Link>{" "}
            {t("planner.saveItinerary")}
          </p>
        ) : null}
      </form>
      {error ? <p className="mt-4 text-sm text-red-700">{error}</p> : null}
      {itineraryId ? (
        <p className="mt-4 text-sm text-pine">
          {t("planner.saveItinerary")}: {itineraryId}
        </p>
      ) : null}
      {savedList.length ? (
        <div className="mt-8">
          <h2 className="font-display text-xl font-bold">{t("saved.itineraries")}</h2>
          <ul className="mt-3 space-y-2">
            {savedList.map((it) => (
              <li key={String(it.id)} className="rounded border border-mist bg-white px-3 py-2 text-sm">
                {String(it.name || it.id)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        {recs.map((d) => (
          <div key={d.id} className="relative">
            <ContentCard
              href={`${base}/destinations/${d.slug}`}
              title={d.name}
              image={d.image}
              demo={d.isDemo}
              demoLabel={t("common.demoListing")}
              meta={[d.score != null ? `score ${d.score}` : ""].filter(Boolean)}
            />
            <div className="absolute end-3 top-3 z-10">
              <FavoriteSave type="destination" id={d.id} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function NearbyPage() {
  const { t, locale } = useI18n();
  const base = useLocalePath();
  const [items, setItems] = useState<Item[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    nearbyApi
      .query({
        latitude: 36.1911,
        longitude: 44.0092,
        radius: 80,
        type: "destination",
        language: locale,
      })
      .then((res) => setItems(normalizeList(res.data.items)))
      .catch((e) => setError(e instanceof Error ? e.message : t("common.error")))
      .finally(() => setLoading(false));
  }, [locale, t]);

  return (
    <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <h1 className="font-display text-4xl font-bold">{t("common.nearby")}</h1>
      <p className="mt-2 text-sm text-ridge">Erbil · 80km · destinations</p>
      <StateBox loading={loading} error={error} empty={!items.length}>
        <ul className="mt-8 space-y-3">
          {items.map((item) => (
            <li key={item.id} className="rounded border border-mist bg-white p-4">
              <Link className="font-semibold text-pine" to={`${base}/destinations/${item.slug}`}>
                {item.name}
              </Link>
              {item.distanceKm != null ? (
                <span className="ms-3 text-sm text-ridge">{item.distanceKm} km</span>
              ) : null}
            </li>
          ))}
        </ul>
      </StateBox>
    </section>
  );
}

export function LoginPage() {
  const { t } = useI18n();
  const { login } = useAuth();
  const base = useLocalePath();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  return (
    <section className="mx-auto max-w-md px-4 py-16">
      <h1 className="font-display text-3xl font-bold">{t("auth.loginTitle")}</h1>
      <form
        className="mt-8 space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          const fd = new FormData(e.currentTarget);
          try {
            await login(String(fd.get("email")), String(fd.get("password")));
            navigate(`${base}/profile`);
          } catch (err) {
            setError(err instanceof Error ? err.message : t("common.error"));
          }
        }}
      >
        <input name="email" type="email" required placeholder={t("auth.email")} className="w-full rounded border border-mist px-3 py-2" />
        <input name="password" type="password" required placeholder={t("auth.password")} className="w-full rounded border border-mist px-3 py-2" />
        {error ? <p className="text-sm text-red-700">{error}</p> : null}
        <button type="submit" className="w-full rounded-md bg-pine py-2.5 font-semibold text-sand">
          {t("auth.submitLogin")}
        </button>
      </form>
      <p className="mt-4 text-sm">
        <Link to={`${base}/register`} className="text-pine underline">
          {t("nav.register")}
        </Link>
      </p>
    </section>
  );
}

export function RegisterPage() {
  const { t } = useI18n();
  const { register } = useAuth();
  const base = useLocalePath();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  return (
    <section className="mx-auto max-w-md px-4 py-16">
      <h1 className="font-display text-3xl font-bold">{t("auth.registerTitle")}</h1>
      <form
        className="mt-8 space-y-4"
        onSubmit={async (e) => {
          e.preventDefault();
          const fd = new FormData(e.currentTarget);
          try {
            await register(
              String(fd.get("email")),
              String(fd.get("password")),
              String(fd.get("name") || ""),
            );
            navigate(`${base}/profile`);
          } catch (err) {
            setError(err instanceof Error ? err.message : t("common.error"));
          }
        }}
      >
        <input name="name" placeholder={t("auth.name")} className="w-full rounded border border-mist px-3 py-2" />
        <input name="email" type="email" required placeholder={t("auth.email")} className="w-full rounded border border-mist px-3 py-2" />
        <input name="password" type="password" required minLength={8} placeholder={t("auth.password")} className="w-full rounded border border-mist px-3 py-2" />
        {error ? <p className="text-sm text-red-700">{error}</p> : null}
        <button type="submit" className="w-full rounded-md bg-pine py-2.5 font-semibold text-sand">
          {t("auth.submitRegister")}
        </button>
      </form>
    </section>
  );
}
